import { PromiseQueue } from "./promiseQueue.js";

function cropRGBAtoROI(rgba, frameWidth, frameHeight, x0, y0, roiWidth, roiHeight) {
  // Reject non-finite or non-positive dimensions first. NaN/Infinity silently
  // pass the < / > comparisons below (NaN comparisons are always false), and a
  // non-finite or negative size would then throw in the Uint8ClampedArray
  // allocation. Integers are expected (the caller floors/ceils the ROI).
  if (
    !Number.isInteger(x0) ||
    !Number.isInteger(y0) ||
    !Number.isInteger(roiWidth) ||
    !Number.isInteger(roiHeight) ||
    roiWidth <= 0 ||
    roiHeight <= 0
  ) {
    console.error("Invalid crop dimensions.");
    return null;
  }
  if (x0 < 0 || y0 < 0 || x0 + roiWidth > frameWidth || y0 + roiHeight > frameHeight) {
    console.error("Crop region is out of bounds.");
    return null;
  }

  const cropped = new Uint8ClampedArray(roiWidth * roiHeight * 4);
  for (let row = 0; row < roiHeight; row++) {
    const srcStart = ((y0 + row) * frameWidth + x0) * 4;
    cropped.set(rgba.subarray(srcStart, srcStart + roiWidth * 4), row * roiWidth * 4);
  }
  return cropped;
}

class DataLogger {
  static experience = "7f19f337-3d35-4e13-b6c7-d1b1b4d088f0";
  static authToken = "a5acdb33f998ec836a4c736aaf72405d9e636f1c";
  static bufferLimit = 10;
  // Cap for the RGBA stash. Frames are stashed at uploadFps (~5/s) and cleared
  // when the scan engine reports a carrier for them; frames that never get a
  // carrier callback would otherwise accumulate for the whole attempt. This
  // bounds the scan-to-carrier lag window (~3 s of frames), separate from the
  // upload-queue depth (bufferLimit).
  static rgbaStashLimit = 15;

  constructor(sessionId, willUploadFrames = false, cropFramesToROI = false) {
    this.sessionId = sessionId;
    this.attemptId = 0;

    this.willUploadFrames = willUploadFrames;
    // When false (the default), the full frame is uploaded. When true, only
    // the crop box reported by the carriers callback (QR widened to include
    // the secure graphic region) is cropped out and uploaded.
    this.cropFramesToROI = cropFramesToROI;
    // Per-upload timeout. Crops are a few hundred KB and median around
    // 300 ms, so 3 s marks one as stuck. Full frames are megabytes even as
    // JPEG and can need several seconds on an ordinary mobile uplink, so
    // they get a longer leash before their queue slot is given up.
    this.uploadTimeoutMs = cropFramesToROI ? 3000 : 10000;
    this.initAttemptVariables();

    this.logPromise = null;

    this.frameData = {
      frameId: null,
      selectedCameraIndex: null,
      isTorchEnabled: null,
      blurScore: null,
      blurThreshold: null,
      glareScore: null,
      glareThreshold: null,
      imageSize: null,
      carrierPatternCenters: null,
      secureGraphicROI: null,
      sizeResult: null,
      sizeLowerLimit: null,
      sizeUpperLimit: null,
      codeContent: null,
      codeState: null,
      messageToUser: null
    };
  }

  initAttemptVariables() {
    if (this.willUploadFrames) {
      // Terminate the previous attempt's worker. A worker is a live thread
      // with its own heap, so dropping the reference alone does not free it,
      // and attempt counters bump on every retry. The terminate is deferred a
      // little past the per-upload timeout so uploads already posted to the
      // old worker can finish instead of dying mid-POST. Only one old worker
      // may drain at a time: another bump inside the window terminates the
      // previous one immediately, so rapid retries cannot stack live threads.
      if (this.frameUploadWorker) {
        if (this.drainingWorker) {
          clearTimeout(this.drainingWorker.timer);
          this.drainingWorker.worker.terminate();
        }
        const oldWorker = this.frameUploadWorker;
        this.drainingWorker = {
          worker: oldWorker,
          timer: setTimeout(() => {
            oldWorker.terminate();
            this.drainingWorker = null;
          }, this.uploadTimeoutMs + 500)
        };
      }
      const newWorker = new Worker(new URL("./frameUploadWorker.js", import.meta.url), { type: "module" });
      newWorker.onerror = (error) => {
        console.error("Frame Upload worker init error:\n", error);
        // Latch the failure. A module-load error fires this once, usually
        // before any upload has attached its per-upload error listener, so
        // uploads posted afterwards would each burn the full timeout against
        // a dead worker. uploadFrame checks the flag and fails fast instead.
        newWorker.hasFailed = true;
      };
      this.frameUploadWorker = newWorker;
      // Cancel the abandoned queue's not-yet-started tasks. Their uploads
      // would no longer be claimed in the log (wrong attempt), and in
      // full-frame mode each pending closure pins a whole frame, so rapid
      // retries would stack tens of MB per abandoned queue for nothing.
      // The task already running is left to finish inside the drain window.
      if (this.inputFramesQueue) {
        this.inputFramesQueue.cancelPending();
      }
      this.inputFramesQueue = new PromiseQueue(DataLogger.bufferLimit);
      this.inputFramesRGBAMap = new Map();
      // Reset the presign state per attempt on purpose. A failed fetch is
      // retried on the next attempt instead of disabling the session, and a
      // long-lived session (bfcache return) picks up fresh presigned POST
      // fields before their policy expires.
      this.attemptedFetchFrameUploadData = false;
      this.frameUploadData = null;
    }
    this.inputFrameId = 0;
    this.timeToStartBenchmark = null;
    this.timeToFirstReadableFrame = null;
    this.timeToAuthCandidate = null;
    this.timeToResponseReceived = null;
    this.authTimeStamp = null;
    this.scanningCodeContent = null;
    this.scannedFramesData = [];
    this.inputFramesSendTimes = [];
    this.inputFramesProcessingTimes = [];
    this.inputFramesURLs = [];
    this.scannedFrameIDCounter = 0;
  }

  renewAttempt() {
    // Variable that don't need to be same across attempts.
    this.initAttemptVariables();

    this.resetFrameData();
  }

  getSessionId() {
    return this.sessionId;
  }

  getAttemptId() {
    return this.attemptId;
  }

  getInputFrameId() {
    return this.inputFrameId;
  }

  incrementInputFrameId() {
    this.inputFrameId += 1;
  }

  incrementAttemptId() {
    this.attemptId += 1;
    this.renewAttempt();
  }

  getDataLoggerExperience() {
    return DataLogger.experience;
  }

  getAuthToken() {
    return DataLogger.authToken;
  }

  recordTimeStartBenchmark(renew = false) {
    if (renew || !this.timeToStartBenchmark) {
      this.timeToStartBenchmark = performance.now();
    }
  }

  recordTimeFirstReadableFrame(renew = false) {
    if (renew || !this.timeToFirstReadableFrame) {
      this.timeToFirstReadableFrame = performance.now();
    }
  }

  recordTimeAuthCandidate(renew = false) {
    if (renew || !this.timeToAuthCandidate) {
      this.timeToAuthCandidate = performance.now();
    }
  }

  recordTimeResponseReceived(renew = false) {
    if (renew || !this.timeToResponseReceived) {
      this.timeToResponseReceived = performance.now();
    }
  }

  getTimeStartToRead() {
    return this.timeToFirstReadableFrame - this.timeToStartBenchmark;
  }

  getTimeReadToAuthCandidate() {
    return this.timeToAuthCandidate - this.timeToFirstReadableFrame;
  }

  getTimeAuthCandidateToResponseReceived() {
    return this.timeToResponseReceived - this.timeToAuthCandidate;
  }

  addFrameData() {
    const frameWithId = {
      ...this.frameData,
      id: this.scannedFrameIDCounter++
    };
    this.scannedFramesData.push(frameWithId);
    this.resetFrameData();
  }

  resetFrameData() {
    for (let property in this.frameData) {
      if (property === "messageToUser") {
        continue;
      }
      this.frameData[property] = null;
    }
  }

  processSDKTime() {
    for (let i = 0; i < this.inputFramesSendTimes.length - 1; i++) {
      let t = this.inputFramesSendTimes[i + 1] - this.inputFramesSendTimes[i];
      this.inputFramesProcessingTimes.push(t);
    }
  }

  async logScanData(
    uuid,
    deviceModel,
    deviceConfigured,
    browser,
    browserVersion,
    browserEngine,
    scanningURL,
    appVersion,
    availableCamerasIds,
    selectedCameraCapabilities,
    videoTrackSettings,
    authResult,
    frameId,
    authFailureMode,
    endSessionMode
  ) {
    this.processSDKTime();

    const experience = this.getDataLoggerExperience();
    const token = this.getAuthToken();

    const sessionId = this.getSessionId();

    const dataToSend = {
      experience: experience,
      key: {
        uuid: uuid,
        hasFrameImages: this.willUploadFrames && this.inputFramesURLs.length > 0,
        deviceModel: deviceModel,
        browser: browser,
        sessionId: sessionId
      },
      data: {
        deviceModel: deviceModel,
        browser: browser,
        browserVersion: browserVersion,
        browserEngine: browserEngine,
        scanningURL: scanningURL,
        appVersion: appVersion,
        availableCamerasIds: availableCamerasIds, // availableCameras.value,
        selectedCameraCapabilities: selectedCameraCapabilities, // authenticator.getCamera().getCapabilities(),
        videoTrackSettings: videoTrackSettings, //authenticator.getCamera().getVideoTrackSettings(),
        timeStartToRead: this.getTimeStartToRead(),
        timeReadToAuthCandidate: this.getTimeReadToAuthCandidate(),
        timeAuthCandidateToResponseReceived: this.getTimeAuthCandidateToResponseReceived(),
        authTimeStamp: this.authTimeStamp,
        authResult: authResult,
        sentFrameId: frameId,
        authFailureMode: authFailureMode,
        framesInformation: this.scannedFramesData,
        endSessionMode: endSessionMode,
        sessionId: sessionId,
        attemptId: this.getAttemptId(),
        deviceConfigured: deviceConfigured,
        uploadInputFrames: this.willUploadFrames, // This is for the branch where frames are logged.
        // Whether the uploaded images are crops (QR box widened to include
        // the secure graphic) or full frames, so log consumers can map the
        // frame-coordinate fields onto the images correctly.
        uploadedFramesCropped: this.cropFramesToROI,
        inputFramesProcessingTimes: this.inputFramesProcessingTimes,
        inputFramesURLs: this.inputFramesURLs,
        inputFramesBufferLimit: DataLogger.bufferLimit
      }
    };

    const apiHost = "rnd.scantrust.io";
    this.logPromise = fetch(`https://${apiHost}/api/data-logger/log/`, {
      credentials: "same-origin",
      mode: "cors",
      method: "post",
      headers: { "Content-Type": "application/json", Authorization: `Token ${token}` },
      body: JSON.stringify(dataToSend)
    })
      .then((resp) => {
        if (resp.ok) {
          return resp.json();
        } else {
          console.error("Status: " + resp.status);
          return Promise.reject(new Error("server"));
        }
      })
      .then((dataJson) => {
        console.log("Logged sucessfully");
      })
      .catch((err) => {
        console.error("Logging error: " + err);
        if (err === "server") return;
      });
    return this.logPromise;
  }

  async fetchFrameUploadData() {
    const experience = this.getDataLoggerExperience();
    const endpoint = `https://rnd.scantrust.io/api/data-logger/upload/${experience}/`;
    this.attemptedFetchFrameUploadData = true;

    try {
      const response = await fetch(endpoint);
      if (!response.ok) {
        console.log("Frame upload data status: " + response.status);
        return;
      }
      const data = await response.json();
      this.frameUploadData = data;
    } catch (err) {
      console.log("Frame upload data error: " + err);
    }
  }

  // Stash the analyzed frame's RGBA so it can be uploaded later (if the
  // scan engine reports a QR carrier on that frame). Frames that never
  // produce a carrier callback are released by initAttemptVariables on
  // the next attempt.
  storeFrame(rgba, frameWidth, frameHeight) {
    const frameId = this.inputFrameId;
    const attempt = this.attemptId;
    const id = `${attempt}-${frameId}`;
    // Evict the oldest stashed frame(s) once at the cap. Map preserves
    // insertion order, so keys().next() is the oldest. This keeps the stash
    // bounded when frames are stored but no carrier callback ever consumes them.
    while (this.inputFramesRGBAMap.size >= DataLogger.rgbaStashLimit) {
      const oldest = this.inputFramesRGBAMap.keys().next().value;
      this.inputFramesRGBAMap.delete(oldest);
    }
    this.inputFramesRGBAMap.set(id, { rgba, frameWidth, frameHeight });
  }

  // Called from VideoAuth.vue's carriers callback with the raw crop box
  // (QR corners widened by the secure graphic region). Looks up the stashed
  // RGBA for (attempt, frameId) and queues its upload.
  //
  // Memory cost per queued closure differs by mode. Default (full frame):
  // the closure pins the whole frame, ~8 MB at 1080p, bounded by
  // bufferLimit. Crop mode: the box is clamped to the frame and cropped
  // here on the main thread (a sub-millisecond row copy at upload FPS), so
  // the closure holds only a few hundred KB.
  //
  // NOTE for the planned per-frame ROI logging: the box parameters are
  // intentionally unused in full-frame mode, so log the (clamped) box
  // UNCONDITIONALLY here, not inside the crop branch, or full-frame
  // sessions will silently log no ROI.
  pushToFramesQueue(attempt, frameId, minX, minY, maxX, maxY) {
    const id = `${attempt}-${frameId}`;
    const data = this.inputFramesRGBAMap.get(id);
    if (!data) {
      // Frame was throttled out at storeFrame, or already consumed.
      return;
    }
    this.inputFramesRGBAMap.delete(id);

    const { rgba, frameWidth, frameHeight } = data;
    let uploadRGBA = rgba;
    let uploadWidth = frameWidth;
    let uploadHeight = frameHeight;
    if (this.cropFramesToROI) {
      // Clamp to the frame. The SDK projects the code rectangle through a
      // homography with no bounds clamp, so corners of a code touching the
      // frame edge routinely land slightly outside the frame.
      const x0 = Math.max(0, minX);
      const y0 = Math.max(0, minY);
      const roiWidth = Math.min(frameWidth, maxX) - x0;
      const roiHeight = Math.min(frameHeight, maxY) - y0;
      const rgbaROI = cropRGBAtoROI(rgba, frameWidth, frameHeight, x0, y0, roiWidth, roiHeight);
      if (!rgbaROI) {
        console.log(`Attempt ${attempt} frame ${frameId}: invalid ROI, skipping upload.`);
        return;
      }
      uploadRGBA = rgbaROI;
      uploadWidth = roiWidth;
      uploadHeight = roiHeight;
    }

    const localThis = this;
    this.inputFramesQueue
      .add(() => localThis.uploadFrame(frameId, attempt, uploadRGBA, uploadWidth, uploadHeight))
      .catch((error) => console.log(`Attempt ${attempt} frame ${frameId}: error while adding it in the queue: \n ${error}`));
  }

  extractS3FileURL(postURL, uploadPath) {
    const url = new URL(postURL);
    const subdomain = url.hostname.split(".")[0];
    const s3FileURL = `s3://${subdomain}/${uploadPath}`;
    return s3FileURL;
  }

  async uploadFrame(id, attempt, rgba, width, height) {
    // Only attempt to fetch frame upload data one time.
    // If it fails we will not upload any frames and not try again to minimize impact on UX.
    if (this.frameUploadData === null) {
      if (this.attemptedFetchFrameUploadData) {
        return Promise.reject(new Error(""));
      } else {
        await this.fetchFrameUploadData();
      }
    }

    let frameIDString = id.toString().padStart(6, "0");
    let s3FilePath = `${this.frameUploadData["upload_path"]}${this.sessionId}/${attempt}/${frameIDString}.jpg`;
    let postDataURL = this.frameUploadData["post_data"]["url"];
    let s3URL = this.extractS3FileURL(postDataURL, s3FilePath);
    let frameUploadFields = this.frameUploadData["post_data"]["fields"];

    // Pin the worker this upload posts to. If an attempt bump swaps
    // this.frameUploadWorker while this upload is in flight, the message
    // handler and its cleanup must still target the worker the message was
    // posted to. Tasks queued before a bump never reach this point: the
    // bump cancels them (see initAttemptVariables), so only the task
    // already running can span a bump.
    const worker = this.frameUploadWorker;
    if (worker.hasFailed) {
      // The worker never loaded. A posted message would vanish and burn the
      // full per-upload timeout, so fail fast and keep the queue moving.
      return Promise.reject(new Error(`Frame upload worker unavailable: ${s3FilePath}`));
    }

    return new Promise((resolve, reject) => {
      let messageHandler;
      let errorHandler;
      let timeoutHandle;

      // Every exit path detaches both listeners so they don't accumulate
      // across uploads.
      const cleanup = () => {
        clearTimeout(timeoutHandle);
        worker.removeEventListener("message", messageHandler);
        worker.removeEventListener("error", errorHandler);
      };

      // Match the worker's response by s3FilePath.
      messageHandler = (event) => {
        if (event.data.s3FilePath === s3FilePath) {
          cleanup();
          if (event.data.success) {
            // Claim the URL in the log only once the upload is confirmed, and
            // only for the attempt it belongs to. Claiming at dispatch listed
            // frames that a timeout, failure, or the exit navigation then
            // killed, so the log pointed at S3 objects that never existed.
            if (attempt === this.attemptId) {
              this.inputFramesURLs.push(s3URL);
            }
            resolve(event.data.result);
          } else {
            reject(event.data.error);
          }
        }
      };
      worker.addEventListener("message", messageHandler);

      // A top-level worker failure (for example the module failing to load)
      // fires an error event and no message would ever arrive. Rejecting
      // right away frees the serial queue slot instead of burning the full
      // per-upload timeout, which is 10 s in full-frame mode. The queue is
      // serial, so the one in-flight upload is the one this error belongs to.
      errorHandler = () => {
        cleanup();
        reject(new Error(`Frame upload worker error: ${s3FilePath}`));
      };
      worker.addEventListener("error", errorHandler);

      // Safety net: reject after the mode-sized timeout (see constructor) so
      // a stuck upload can't pin its queue slot forever. The 1 s post-scan
      // drain wouldn't have waited for it anyway.
      timeoutHandle = setTimeout(() => {
        cleanup();
        reject(new Error(`Frame upload timeout: ${s3FilePath}`));
      }, this.uploadTimeoutMs);

      // Transfer the buffer instead of copying it. rgba is either the stashed
      // full frame or a fresh crop from cropRGBAtoROI, both plain detachable
      // ArrayBuffers; if a future caller passes something else, failing
      // loudly here is better than silently structured-cloning megabytes.
      // timeoutMs lets the worker abort its fetch in step with this timeout,
      // so a stalled POST does not linger after the slot has been given up.
      const message = { id, attempt, s3FilePath, frameUploadFields, postDataURL, rgba, width, height, timeoutMs: this.uploadTimeoutMs };
      worker.postMessage(message, [rgba.buffer]);
    });
  }

  getFramesQueue() {
    return this.inputFramesQueue ?? undefined;
  }

  async allFramesUploaded(timeoutMilliseconds = -1) {
    // Await sending of all the frames used, capped at `timeoutMilliseconds`
    // so the post-scan flow doesn't block on slow uploads (PRD: ≤ 1 s extra).
    console.log(`Awaiting frame uploads (timeout: ${timeoutMilliseconds} ms)`);
    const q = this.getFramesQueue();
    await q.allSettled(timeoutMilliseconds);
    console.log("Frame upload await completed.");
  }

  async allLogsUploaded() {
    // Await logScanData promise
    if (this.logPromise !== null) {
      await this.logPromise;
      this.logPromise = null;
    }
  }
}

export default DataLogger;
